import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { focusManager } from "@tanstack/react-query";
import { useAuth } from "./useAuth";
import { useForgetRoleOnSignOut } from "./useUserRole";
import { loadWorkspaceOrgs, type WorkspaceAuthority } from "@/lib/orgWorkspaces";

/**
 * Active-org source of truth.
 *
 * Org admins can administer more than one organization. Every admin surface
 * reads "which org am I administering" through `useUserOrg`, and the agent feed
 * rail reads the same lookup through `useOrgsToFollow`. This context is the
 * single place that selection is resolved and persisted so those hooks (and the
 * ~30 surfaces downstream of `useUserOrg`) all honor the same active org.
 *
 * Resolution rules:
 *  - Workspace orgs = `get_my_org_workspaces()` (lib/orgWorkspaces): current
 *    memberships with role in (super_admin, admin, editor), plus the managed
 *    offices of a parent company the user owns or administers, ordered
 *    deterministically by the org's `created_at` ascending. Editors are included
 *    so the profile editor and content pages can resolve which org they edit;
 *    the `role` on each org distinguishes an editor's workspace from an admin's,
 *    and admin-only routes gate on the platform role regardless.
 *  - The active org is the one whose id is persisted in localStorage
 *    (`compaire_active_org`) *if* the user is still a workspace member of it;
 *    otherwise it falls back deterministically to the first workspace org by
 *    `created_at` — never an arbitrary row.
 *
 * Users with no workspace membership (plain agents, admins still in setup)
 * resolve to an empty list / null active org with `isLoading: false`, so the
 * agent path and the route gates render without hanging.
 */

import {
  ACTIVE_ORG_STORAGE_KEY,
  readStoredActiveOrgId,
  selectActiveOrg,
} from "@/lib/activeOrgSelection";

const ACTIVE_ORG_KEY = ACTIVE_ORG_STORAGE_KEY;

export interface AdminOrg {
  id: string;
  name: string;
  slug: string;
  type: string;
  logo_url: string | null;
  /**
   * The user's role in this org (super_admin | admin | editor). A parent
   * company's owner or admin is "admin" in its managed offices; see
   * {@link authority}.
   */
  role: string;
  created_at: string;
  /** The parent company, when this organization is linked to one. */
  parent_org_id?: string | null;
  /** Where the role comes from: a direct membership, or the parent company. */
  authority?: WorkspaceAuthority;
  /** The current direct membership role, if any. */
  direct_role?: string | null;
  /** The role reported through the parent company ("owner" | "admin"), if any. */
  parent_role?: "owner" | "admin" | null;
}

interface ActiveOrgContextValue {
  /** Orgs the user admins, ordered by created_at ascending. */
  adminOrgs: AdminOrg[];
  /** The currently active admin org, or null when the user admins none. */
  activeOrg: AdminOrg | null;
  /** Id of the active org, or null. */
  activeOrgId: string | null;
  /**
   * True only while the org list has *never* resolved — i.e. there is nothing
   * to show yet. A background re-resolution leaves this false and keeps the
   * previously fetched org rendered; watch {@link isRefreshing} for that.
   */
  isLoading: boolean;
  /**
   * True while a re-resolution runs on top of already-loaded data. Purely
   * informational (a subtle indicator at most) — never gate rendering on it,
   * or a routine token refresh blanks the surface again.
   */
  isRefreshing: boolean;
  /** Switch the active org (must be one the user admins); persists the choice. */
  setActiveOrg: (id: string) => void;
  /**
   * Re-resolve the admin org list from the database. Resolves with what was
   * read, so a caller that must not proceed on a failed read can tell.
   * `preferOrgId` makes that org the active one when it is in the fresh list
   * (the claim page's approval hand-off); otherwise the stored choice applies.
   */
  refetch: (options?: { preferOrgId?: string | null }) => Promise<WorkspaceRefreshResult>;
}

export type WorkspaceRefreshResult =
  | { ok: true; orgs: AdminOrg[]; activeOrgId: string | null }
  | { ok: false };

const ActiveOrgContext = createContext<ActiveOrgContextValue | undefined>(
  undefined,
);

const readStoredOrgId = readStoredActiveOrgId;

const writeStoredOrgId = (id: string) => {
  try {
    localStorage.setItem(ACTIVE_ORG_KEY, id);
  } catch {
    /* storage unavailable — selection is in-memory only this session */
  }
};

export const ActiveOrgProvider = ({ children }: { children: ReactNode }) => {
  const { user, isLoading: authLoading } = useAuth();
  // Depend on the id, not the user object. Supabase hands back a freshly
  // deserialized session (and therefore a new `user` reference) on every
  // TOKEN_REFRESHED/SIGNED_IN — which fires whenever the tab regains focus.
  // Keying on the object made that identity change refetch the org list on
  // every tab switch; the id is stable across refreshes for the same account.
  const userId = user?.id ?? null;
  const [adminOrgs, setAdminOrgs] = useState<AdminOrg[]>([]);
  const [activeOrgId, setActiveOrgId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  /** Which user id the current adminOrgs/activeOrgId state was resolved for. */
  const loadedForUserRef = useRef<string | null>(null);
  /** Reads in flight, so a focus event does not stack another one on them. */
  const inFlightRef = useRef(0);
  /**
   * Counts explicit reads (the initial load, a user change, `refetch()`). A
   * focus re-read remembers the count it started under and is dropped when an
   * explicit read began after it, so an older automatic answer never lands on
   * top of a newer requested one, whatever order they resolve in.
   */
  const explicitSeqRef = useRef(0);
  /**
   * The user this render is for. A read started for an earlier user (signed
   * out, or switched account, while it was in flight) checks this when it
   * lands and changes nothing, so one account's orgs never show in another's.
   */
  const currentUserRef = useRef<string | null>(userId);
  currentUserRef.current = userId;
  /**
   * The org on screen, so a focus re-read can keep it (see below). Written
   * wherever the active org is set, not only on render, so a selection made
   * while a re-read is in flight is what that re-read sees when it lands.
   */
  const activeOrgIdRef = useRef<string | null>(null);
  const applyActiveOrgId = useCallback((id: string | null) => {
    activeOrgIdRef.current = id;
    setActiveOrgId(id);
  }, []);

  // This provider lives for the whole app, so it is where the role cache is
  // dropped for a user who signed out (see useForgetRoleOnSignOut).
  useForgetRoleOnSignOut(userId);

  const fetchOrgs = useCallback(async (
    options?: { preferOrgId?: string | null },
    /**
     * Internal (the focus re-read): when the answer lands, keep the org this
     * tab has active *then* if it is still in the fresh list, without reading
     * or writing the stored choice. The stored id is shared by every tab, so
     * honouring it here would switch this tab to whatever another tab chose
     * last, mid-edit; reading the selection at response time (not request
     * time) keeps a choice made while the read was in flight.
     */
    keepOnScreen = false,
  ): Promise<WorkspaceRefreshResult> => {
    // `options` may be anything when refetch is passed straight to an event
    // handler; only a string id is honoured.
    const preferOrgId = typeof options?.preferOrgId === "string" ? options.preferOrgId : null;
    if (!keepOnScreen) explicitSeqRef.current += 1;
    const startedUnder = explicitSeqRef.current;
    if (!userId) {
      loadedForUserRef.current = null;
      setAdminOrgs([]);
      applyActiveOrgId(null);
      setIsLoading(false);
      setIsRefreshing(false);
      return { ok: false };
    }

    // Already resolved for this same user => background refresh: keep the
    // existing org on screen and leave isLoading alone. A different (or first)
    // user => a genuine load, where there is nothing valid to keep showing.
    const isRefresh = loadedForUserRef.current === userId;
    if (isRefresh) {
      setIsRefreshing(true);
    } else {
      setAdminOrgs([]);
      applyActiveOrgId(null);
      setIsLoading(true);
    }

    inFlightRef.current += 1;
    /** False once the signed-in user is no longer the one this read is for. */
    const stillCurrent = () => currentUserRef.current === userId;
    /** False for a focus re-read overtaken by a later explicit read. */
    const notOvertaken = () => explicitSeqRef.current === startedUnder;
    try {
      // get_my_org_workspaces (office profiles, spec 7.3): direct workspaces
      // plus the managed offices a parent owner or admin reaches. Eligibility
      // and ordering live in lib/orgWorkspaces, shared with the post-login
      // destination.
      const { orgs, error } = await loadWorkspaceOrgs(userId);
      if (!stillCurrent() || !notOvertaken()) return { ok: false };
      if (error) throw error;
      const ordered: AdminOrg[] = orgs;
      // An unchanged list (the usual focus re-read) keeps its identity, so
      // nothing downstream re-renders for it.
      setAdminOrgs((current) => (JSON.stringify(current) === JSON.stringify(ordered) ? current : ordered));

      const onScreen = keepOnScreen ? activeOrgIdRef.current : null;
      if (onScreen && ordered.some((o) => o.id === onScreen)) {
        return { ok: true, orgs: ordered, activeOrgId: onScreen };
      }

      const stored = readStoredOrgId();
      const preferred = preferOrgId && ordered.some((o) => o.id === preferOrgId) ? preferOrgId : stored;
      const active = selectActiveOrg(ordered, preferred);
      if (active) {
        applyActiveOrgId(active.id);
        // Persist when falling back (repairing a stale id) or when preferred.
        if (active.id !== stored) writeStoredOrgId(active.id);
      } else {
        applyActiveOrgId(null);
      }
      return { ok: true, orgs: ordered, activeOrgId: active?.id ?? null };
    } catch (err) {
      if (!stillCurrent() || !notOvertaken()) return { ok: false };
      console.error("Error loading admin orgs:", err);
      // A failed *background* refresh must not wipe an org that is still valid
      // and on screen. Only a failed first load clears to the empty state.
      if (!isRefresh) {
        setAdminOrgs([]);
        applyActiveOrgId(null);
      }
      return { ok: false };
    } finally {
      inFlightRef.current -= 1;
      // A read for an earlier user, or one overtaken by a later explicit read,
      // leaves the state that later read owns alone.
      if (stillCurrent() && notOvertaken()) {
        loadedForUserRef.current = userId;
        setIsLoading(false);
      }
      // Whichever read finishes last ends the refresh indicator, even when that
      // read belonged to an earlier user: with nothing in flight there is
      // nothing refreshing for anyone.
      if (inFlightRef.current === 0) setIsRefreshing(false);
    }
  }, [userId, applyActiveOrgId]);

  useEffect(() => {
    if (!authLoading) fetchOrgs();
  }, [authLoading, fetchOrgs]);

  // A workspace can be granted outside this tab (a founder approving a claim),
  // so re-read the list when the tab comes back into focus, as useUserRole
  // re-reads the role. It is a background refresh that keeps the org on screen
  // while it is still in the list; only when it is gone does it fall back as a
  // first load would. Explicit refetch() callers keep the stored-choice rule.
  useEffect(() => {
    return focusManager.subscribe((focused) => {
      if (!focused || !userId || inFlightRef.current > 0) return;
      if (loadedForUserRef.current !== userId) return;
      void fetchOrgs(undefined, true);
    });
  }, [userId, fetchOrgs]);

  const setActiveOrg = useCallback(
    (id: string) => {
      // Only accept an org the user actually admins.
      if (!adminOrgs.some((o) => o.id === id)) return;
      applyActiveOrgId(id);
      writeStoredOrgId(id);
    },
    [adminOrgs, applyActiveOrgId],
  );

  const activeOrg = useMemo(
    () => adminOrgs.find((o) => o.id === activeOrgId) ?? null,
    [adminOrgs, activeOrgId],
  );

  const value = useMemo<ActiveOrgContextValue>(
    () => ({
      adminOrgs,
      activeOrg,
      activeOrgId,
      isLoading: authLoading || isLoading,
      isRefreshing,
      setActiveOrg,
      // Never forwards a second argument: keeping the on-screen org is the
      // focus re-read's rule only.
      refetch: (options) => fetchOrgs(options),
    }),
    [
      adminOrgs,
      activeOrg,
      activeOrgId,
      authLoading,
      isLoading,
      isRefreshing,
      setActiveOrg,
      fetchOrgs,
    ],
  );

  return (
    <ActiveOrgContext.Provider value={value}>
      {children}
    </ActiveOrgContext.Provider>
  );
};

/** The active-org context, or undefined outside its provider (for hooks that can run either way). */
export const useOptionalActiveOrg = (): ActiveOrgContextValue | undefined => useContext(ActiveOrgContext);

export const useActiveOrg = (): ActiveOrgContextValue => {
  const ctx = useContext(ActiveOrgContext);
  if (!ctx) {
    throw new Error("useActiveOrg must be used within an ActiveOrgProvider");
  }
  return ctx;
};
